/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd. <legal@vecros.com>
 * Proprietary and confidential.
 **********************************************************************************/

import { useCallback, useState } from "react";
import { listDevices } from "../containers/newBackendApi/newDevices";
import { listLogs } from "../containers/components/logAnalyzer/api/newTelemetry";

// Module-level cache so records are fetched once per session, shared across mounts.
let cache = null;
let inflight = null;

const deviceName = (d) => d.device_name || d.deviceName || d.name || "Device";
const deviceId = (d) => d.device_id || d.deviceId || d.id || "";

/**
 * Lazily fetch the user's devices and flight logs (once) for the assistant's
 * @-mention picker. The developer product surfaces devices AND parsed flight logs
 * as mentionable records (accounts mentions devices + orgs — a deliberate
 * per-service difference; see CLAUDE.md).
 */
export function useAssistantRecords() {
  const [records, setRecords] = useState(cache || { devices: [], logs: [], orgs: [], loaded: false });

  const load = useCallback(async () => {
    if (cache) {
      setRecords(cache);
      return cache;
    }
    if (!inflight) {
      inflight = Promise.allSettled([listDevices({ limit: 100 }), listLogs({ pageSize: 50 })]).then(([d, l]) => {
        cache = {
          devices: d.status === "fulfilled" ? d.value?.devices || [] : [],
          // Only parsed logs are useful to the assistant's tools.
          logs:
            l.status === "fulfilled" ? (l.value?.logs || []).filter((x) => x.status === "ready") : [],
          orgs: [],
          loaded: true,
        };
        return cache;
      });
    }
    const res = await inflight;
    setRecords(res);
    return res;
  }, []);

  return { ...records, load };
}

// Let callers (e.g. the Log Analyzer) drop a freshly-parsed log into the shared
// cache so it's mentionable immediately without a refetch.
export function primeLogRecord(log) {
  if (!log || log.status !== "ready") return;
  const base = cache || { devices: [], logs: [], orgs: [], loaded: true };
  if (base.logs.some((l) => l.id === log.id)) return;
  cache = { ...base, logs: [log, ...base.logs] };
}

/** Build a flat list of mention candidates matching `query`. */
export function mentionMatches(records, query) {
  const q = (query || "").toLowerCase();
  const devices = (records.devices || [])
    .filter((d) => deviceName(d).toLowerCase().includes(q))
    .slice(0, 6)
    .map((d) => ({ type: "DEVICE", id: deviceId(d), label: deviceName(d) }));
  const logs = (records.logs || [])
    .filter((l) => (l.file_name || "").toLowerCase().includes(q))
    .slice(0, 6)
    .map((l) => ({ type: "LOG", id: l.id, label: l.file_name }));
  return [...devices, ...logs];
}
